import React from 'react';
import { X } from 'lucide-react';
import type { User } from '../../../../types/auth';

interface Props {
  user: User;
  onClose: () => void;
  onConfirm: () => Promise<void>;
}

export default function DeleteUserModal({ user, onClose, onConfirm }: Props) {
  const [isDeactivating, setIsDeactivating] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const handleDeactivate = async () => {
    setError(null);
    setIsDeactivating(true);
    try {
      await onConfirm();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsDeactivating(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900">Deactivate User</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6">
          <p className="text-gray-700">
            Are you sure you want to deactivate the user{' '}
            <span className="font-medium">
              {user.firstName} {user.lastName}
            </span>
            ?
          </p>
          
          <p className="mt-2 text-sm text-gray-500">
            The user will be marked as inactive and will no longer be able to log in to the system.
            You can reactivate the user later if needed.
          </p>

          {error && (
            <div className="mt-4 text-sm text-red-600">
              {error}
            </div>
          )}

          <div className="mt-6 flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleDeactivate}
              disabled={isDeactivating}
              className="px-4 py-2 bg-red-600 text-white rounded-md text-sm font-medium hover:bg-red-700 disabled:opacity-50"
            >
              {isDeactivating ? 'Deactivating...' : 'Deactivate'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
